import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpcClient } from "../lib/trpc";
import { useOrganisation } from "../lib/organisation";
import { Router } from "../App";
import { slugify } from "../lib/slugify";
import { Button } from "../components/button";
import { Modal } from "../components/modal";
import {
  appContainer,
  pageContent,
  pageSection,
  pageSectionHeader,
  pageSectionTitle,
  pageEmpty,
  eventsList,
  eventItem,
  eventItemClickable,
  eventContent,
  eventName,
  eventDate,
  formGroup,
  formLabel,
  formInput,
  formError,
  loading,
  error,
  modalActions,
} from "@/styles/shared";

type Event = {
  id: string;
  name: string;
  date: string;
  createdAt: string;
  updatedAt: string;
};

function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}


export function HomePage() {
  const { t, i18n } = useTranslation();
  const { currentOrganisation } = useOrganisation();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [eventNameValue, setEventNameValue] = useState("");
  const [eventDateValue, setEventDateValue] = useState("");

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const result = await trpcClient.listEvents.query({ limit: 50, offset: 0 });
      setEvents(result);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load events");
      console.error("Error fetching events:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [currentOrganisation?.id]);

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventNameValue.trim() || !eventDateValue) {
      setCreateError(t("events.nameAndDateRequired"));
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await trpcClient.createEvent.mutate({
        name: eventNameValue.trim(),
        date: new Date(eventDateValue).toISOString(),
      });
      setShowCreateModal(false);
      setEventNameValue("");
      setEventDateValue("");
      await fetchEvents();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to create event");
      console.error("Error creating event:", e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div css={appContainer}>
      <main css={pageContent}>
        <section css={pageSection}>
          <div css={pageSectionHeader}>
            <h3 css={pageSectionTitle}>{t("events.title")}</h3>
            <Button onClick={() => setShowCreateModal(true)} variant="primary" size="sm">
              {t("events.createButton")}
            </Button>
          </div>
          {isLoading && <p css={loading}>{t("events.loading")}</p>}
          {err && <p css={error}>{t("common.error", { message: err })}</p>}
          {!isLoading && !err && events.length === 0 && (
            <p css={pageEmpty}>{t("events.empty")}</p>
          )}
          {!isLoading && !err && events.length > 0 && (
            <ul css={eventsList}>
              {events.map((event) => {
                const eventSlug = slugify(event.name);
                return (
                  <li
                    key={event.id}
                    css={[eventItem, eventItemClickable]}
                    onClick={() => {
                      Router.push("Event", { orgSlug: slugify(currentOrganisation?.name ?? ""), name: eventSlug, id: event.id });
                    }}
                  >
                    <div css={eventContent}>
                      <h4 css={eventName}>{event.name}</h4>
                      <p css={eventDate}>{formatDate(event.date, i18n.language)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <Modal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title={t("events.createTitle")}
        >
          <form onSubmit={handleCreateEvent}>
            <div css={formGroup}>
              <label htmlFor="event-name" css={formLabel}>
                {t("events.eventName")}
              </label>
              <input
                id="event-name"
                type="text"
                value={eventNameValue}
                onChange={(e) => setEventNameValue(e.target.value)}
                css={formInput}
                placeholder={t("events.eventNamePlaceholder")}
                required
                disabled={creating}
              />
            </div>
            <div css={formGroup}>
              <label htmlFor="event-date" css={formLabel}>
                {t("events.eventDate")}
              </label>
              <input
                id="event-date"
                type="datetime-local"
                value={eventDateValue}
                onChange={(e) => setEventDateValue(e.target.value)}
                css={formInput}
                required
                disabled={creating}
              />
            </div>
            {createError && <div css={formError}>{createError}</div>}
            <div css={modalActions}>
              <Button
                onClick={() => {
                  setShowCreateModal(false);
                  setEventNameValue("");
                  setEventDateValue("");
                  setCreateError(null);
                }}
                variant="secondary"
                disabled={creating}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" variant="primary" disabled={creating}>
                {creating ? t("events.creating") : t("events.createEvent")}
              </Button>
            </div>
          </form>
        </Modal>
      </main>
    </div>
  );
}
